import {PayoutVerificationResponse} from "../../types";
import {WithdrawReceiptSnapshot, WithdrawResultState} from "./types";
import {WithdrawalIntentSignature} from "./utils";

export type PayoutVerificationState = {
  isSubmittingWithdrawal: boolean, withdrawResult?: WithdrawResultState, receiptSnapshot?: WithdrawReceiptSnapshot,
  isStartingVerification: boolean, payoutVerification?: PayoutVerificationResponse, otpCode: string, otpError: string,
  verificationIntentSignature?: WithdrawalIntentSignature, verificationStartError: string,
  isResendingVerification: boolean, verificationRestartRequired: boolean
};

type FlowAction = | { scope: 'flow', type: 'RESET_VERIFICATION' } | { scope: 'flow', type: 'RESET_FLOW' }
  | { scope: 'flow', type: 'CLEAR_RESULT' };

type VerificationAction =
  | { scope: 'verification', type: 'USE_EXISTING_VERIFICATION', receiptSnapshot: WithdrawReceiptSnapshot }
  | { scope: 'verification', type: 'START_VERIFICATION', receiptSnapshot: WithdrawReceiptSnapshot }
  | { scope: 'verification', type: 'VERIFICATION_STARTED', verification: PayoutVerificationResponse, signature: WithdrawalIntentSignature }
  | { scope: 'verification', type: 'VERIFICATION_START_ERROR', message: string }
  | { scope: 'verification', type: 'VERIFICATION_START_FINISHED' }
  | { scope: 'verification', type: 'SET_OTP_CODE', value: string }
  | { scope: 'verification', type: 'TERMINAL_VERIFICATION_ERROR', message: string }
  | { scope: 'verification', type: 'SET_OTP_ERROR', message: string }
  | { scope: 'verification', type: 'CLEAR_OTP_ERROR' };

type ResendAction = | { scope: 'resend', type: 'RESEND_STARTED' }
  | { scope: 'resend', type: 'RESEND_ERROR', message: string } | { scope: 'resend', type: 'RESEND_FINISHED' };

type SubmissionAction = | { scope: 'submission', type: 'SUBMISSION_STARTED' }
  | { scope: 'submission', type: 'SUBMISSION_RESULT', result: WithdrawResultState }
  | { scope: 'submission', type: 'SUBMISSION_FINISHED' };

export type PayoutVerificationAction = FlowAction | VerificationAction | ResendAction | SubmissionAction;

export const createPayoutVerificationState = (): PayoutVerificationState => ({
  isSubmittingWithdrawal: false, withdrawResult: undefined, receiptSnapshot: undefined, isStartingVerification: false,
  payoutVerification: undefined, verificationIntentSignature: undefined, otpCode: '', otpError: '',
  verificationStartError: '', verificationRestartRequired: false,
  isResendingVerification: false
});

const resetVerificationState = (state: PayoutVerificationState): PayoutVerificationState => ({
  ...state, isSubmittingWithdrawal: false, receiptSnapshot: undefined, isStartingVerification: false,
  payoutVerification: undefined, verificationIntentSignature: undefined, otpCode: '', otpError: '',
  verificationStartError: '', verificationRestartRequired: false, isResendingVerification: false
});

const reduceFlowState = (state: PayoutVerificationState, action: FlowAction): PayoutVerificationState => {
  switch (action.type) {
    case 'RESET_VERIFICATION':
      return resetVerificationState(state);
    case 'RESET_FLOW':
      return createPayoutVerificationState();
    case 'CLEAR_RESULT':
      return {...state, withdrawResult: undefined};
  }
};

const reduceVerificationState = (state: PayoutVerificationState, action: VerificationAction): PayoutVerificationState => {
  switch (action.type) {
    case 'USE_EXISTING_VERIFICATION':
      return {...state, receiptSnapshot: state.receiptSnapshot || action.receiptSnapshot};
    case 'START_VERIFICATION':
      return {
        ...state, isStartingVerification: true, verificationStartError: '', otpError: '',
        receiptSnapshot: action.receiptSnapshot
      };
    case 'VERIFICATION_STARTED':
      return {
        ...state, payoutVerification: action.verification, verificationIntentSignature: action.signature, otpCode: '',
        verificationRestartRequired: false
      };
    case 'VERIFICATION_START_ERROR':
      return {...state, verificationStartError: action.message};
    case 'VERIFICATION_START_FINISHED':
      return {...state, isStartingVerification: false};
    case 'SET_OTP_CODE':
      return {...state, otpCode: action.value, otpError: ''};
    case 'TERMINAL_VERIFICATION_ERROR':
      return {
        ...state, payoutVerification: undefined, verificationIntentSignature: undefined, otpCode: '',
        otpError: action.message, verificationRestartRequired: true, isResendingVerification: false
      };
    case 'SET_OTP_ERROR':
      return {...state, otpError: action.message};
    case 'CLEAR_OTP_ERROR':
      return {...state, otpError: ''};
  }
};

const reduceResendState = (state: PayoutVerificationState, action: ResendAction): PayoutVerificationState => {
  switch (action.type) {
    case 'RESEND_STARTED':
      return {...state, isResendingVerification: true, otpCode: '', otpError: ''};
    case 'RESEND_ERROR':
      return {...state, otpError: action.message};
    case 'RESEND_FINISHED':
      return {...state, isResendingVerification: false};
  }
};

const reduceSubmissionState = (state: PayoutVerificationState, action: SubmissionAction): PayoutVerificationState => {
  switch (action.type) {
    case 'SUBMISSION_STARTED':
      return {...state, isSubmittingWithdrawal: true, otpCode: '', otpError: ''};
    case 'SUBMISSION_RESULT':
      return {...state, withdrawResult: action.result};
    case 'SUBMISSION_FINISHED':
      return {...state, isSubmittingWithdrawal: false};
  }
};

export const payoutVerificationReducer = (state: PayoutVerificationState, action: PayoutVerificationAction): PayoutVerificationState => {
  switch (action.scope) {
    case 'flow':
      return reduceFlowState(state, action);
    case 'verification':
      return reduceVerificationState(state, action);
    case 'resend':
      return reduceResendState(state, action);
    case 'submission':
      return reduceSubmissionState(state, action);
  }
};
